import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import App from './App';
import './styles/global.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
// Toast provider - Web's own implementation
import ToastProvider from './contexts/ToastContext';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Polyfill for React Native Web compatibility
if (typeof global === 'undefined') {
  (window as any).global = window;
}
// Dev-only instrumentation: wrap React.createElement very early to capture
// the first application-level stack frame when a component is created with
// a `pointerEvents` prop. This helps locate sources that forward
// `pointerEvents` as a JSX prop instead of merging into style.pointerEvents.
// Only active in development.
if (import.meta.env.DEV) {
  try {
    const reactAny = React as any;
    if (reactAny && typeof reactAny.createElement === 'function' && !reactAny.createElement.__rnw_instrumented) {
      const _orig = reactAny.createElement.bind(reactAny);
      reactAny.createElement = function createElement_instrument(type: any, props: any, ...rest: any[]) {
        // `useProps` must be visible to the return site below; declare in function scope.
        let useProps: any = props;
        try {
          if (props && props.pointerEvents !== undefined) {
            // Mark and print a compact summary so developer can locate the component
            const compName = (typeof type === 'string')
              ? type
              : (type && (type.displayName || type.name))
                || (type && type.render && (type.render.displayName || type.render.name))
                || '<anonymous>';
            const propKeys = Object.keys(props || {}).slice(0, 30);
            const smallPreview: any = {};
            for (const k of propKeys) {
              try {
                const v = props[k];
                if (v === null || v === undefined) { smallPreview[k] = v; continue; }
                if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
                  smallPreview[k] = v;
                } else if (Array.isArray(v)) {
                  smallPreview[k] = `[array:${v.length}]`;
                } else if (typeof v === 'object') {
                  // keep small objects to at most 3 keys
                  const keys = Object.keys(v).slice(0,3);
                  smallPreview[k] = `object{${keys.join(',')}}`;
                } else if (typeof v === 'function') {
                  smallPreview[k] = `fn(${(v.name||'anon')})`;
                } else {
                  smallPreview[k] = typeof v;
                }
              } catch (e) {
                smallPreview[k] = '<unserializable>';
              }
            }
            const typeInfo = (typeof type === 'string') ? { kind: 'intrinsic', name: type } : { kind: 'component', name: (type && (type.displayName || type.name)) || '<anonymous>' };
            try {
              console.warn('[RNW_INSTRUMENT] Detected pointerEvents on createElement', JSON.stringify({ component: compName, type: typeInfo, pointerEvents: props.pointerEvents, propsPreview: smallPreview }, null, 2));
            } catch (e) {
              // fallback to non-stringified log
              console.warn('[RNW_INSTRUMENT] Detected pointerEvents on createElement', { component: compName, type: typeInfo, pointerEvents: props.pointerEvents, propsPreview: smallPreview });
            }
            // Print stack for locating source file:line. Dev tools will map via sourcemaps if available.
            const stack = new Error().stack || '';
            // Normalize and split stack lines, skip the first frame which is this instrumentation function.
            const lines = stack.split('\n').map((l: string) => l.trim()).filter(Boolean);
            // Find the first line that likely points into the application source (contains src/ or packages/ or .tsx/.ts)
            let suspect: string | null = null;
            for (let i = 1; i < lines.length; i++) {
              const ln = lines[i];
              if (/main\.tsx/.test(ln)) continue; // skip instrumentation frame
              if (/node_modules/.test(ln)) continue; // skip node_modules
              if (/\.(tsx|ts|jsx|js):\d+:\d+/.test(ln) || /packages\//.test(ln) || /src\//.test(ln)) {
                suspect = ln;
                break;
              }
            }
            if (suspect) {
              console.warn('[RNW_INSTRUMENT] suspect frame:', suspect);
            }
            console.warn('[RNW_INSTRUMENT] full stack:\n' + (stack || 'no-stack'));

            // Dev-only sanitization: move pointerEvents into style.pointerEvents so
            // that React Native Web's runtime deprecation warning is not emitted
            // while we continue to find and fix the real source files. This keeps
            // the instrumentation transparent and reversible.
            try {
              // Avoid mutating the original props object (it may be frozen).
              const { pointerEvents, style, ...restProps } = props as any;
              if (pointerEvents !== undefined) {
                // Merge style: handle array and object shapes conservatively.
                let mergedStyle: any;
                if (Array.isArray(style)) {
                  // Append an object with pointerEvents at the end to not override existing entries
                  mergedStyle = [...style, { pointerEvents }];
                } else if (style && typeof style === 'object') {
                  mergedStyle = { ...style, pointerEvents };
                } else {
                  mergedStyle = { pointerEvents };
                }
                useProps = { ...restProps, style: mergedStyle };
                // Log sanitized preview to help verify the runtime sanitization.
                try {
                  console.warn('[RNW_INSTRUMENT] sanitized props passed to createElement', JSON.stringify({ component: compName, pointerEvents, sanitizedStylePreview: Array.isArray(mergedStyle) ? `[array:${mergedStyle.length}]` : mergedStyle }, null, 2));
                } catch (e) {
                  console.warn('[RNW_INSTRUMENT] sanitized props passed to createElement', { component: compName, pointerEvents, sanitizedStylePreview: Array.isArray(mergedStyle) ? `[array:${mergedStyle.length}]` : mergedStyle });
                }
              }
            } catch (e) {
              // If sanitization throws for any reason, fall back to original props.
              useProps = props;
            }
          }
        } catch (e) {
          // swallow instrumentation errors
          console.error('[RNW_INSTRUMENT] instrumentation error', e);
        }
        return _orig(type, useProps, ...rest);
      } as any;
      (reactAny.createElement as any).__rnw_instrumented = true;
      console.log('[RNW_INSTRUMENT] React.createElement instrumented (dev-only)');
    } else {
      console.warn('[RNW_INSTRUMENT] React.createElement not found or already instrumented');
    }
  } catch (err) {
    console.error('[RNW_INSTRUMENT] failed to install instrumentation', err);
  }
}
// Note: dev-only console guard lives in ./devConsoleGuard to ensure a single centralized
// handler is installed as early as possible. Do not duplicate the wrapper here.

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element #root was not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Provider store={store}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </Provider>
  </React.StrictMode>
);

// PWA Service Worker support removed: no SW will be registered from the app.
